import { LinearProgress, Skeleton, Typography, useMediaQuery } from '@mui/material';
import Box from '@mui/material/Box';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface RouteLoaderProps {
  /**
   * When true, render a static visual approximation of the app shell —
   * a left nav rail, a top bar block, and content-area skeleton blocks
   * — so a route transition or auth check feels like the chrome is
   * loading rather than the whole app vanishing.
   *
   * Use for in-app loaders (per-route auth checks, page transitions).
   * Leave off for the cold-boot loader where there's no app context
   * to suggest yet.
   */
  showShell?: boolean;
}

const SHELL_RAIL_WIDTH = 260;
const SHELL_TOPBAR_HEIGHT = 64;
// The real phone shell (AppNav's dense header) is 48px, not the 64px
// desktop top bar.
const SHELL_MOBILE_TOPBAR_HEIGHT = 48;

/**
 * Visual loader for auth checks and cold-boot waits.
 *
 * - Thin LinearProgress at the top of the viewport (immediate).
 * - With `showShell`: static skeleton of the app shell (left rail,
 *   top bar, content blocks) sized to the real LeftNav, so the chrome
 *   doesn't appear to vanish during a route transition.
 * - "Loading..." caption absolutely centered on top of everything.
 *
 * The full-screen container uses the theme background so transitions
 * to/from rendered content don't flash a different color.
 */
const RouteLoader: React.FC<RouteLoaderProps> = ({ showShell = false }) => {
  const { t } = useTranslation();
  // Same cutoff as the real shell (LeftNav/AppNav switch on the custom
  // `mobile` breakpoint) so the skeleton never previews the wrong layout
  // in the 600-767px band.
  const isPhone = useMediaQuery((theme: any) => theme.breakpoints.down('mobile'));
  const showRail = showShell && !isPhone;

  return (
    <Box
      sx={{
        position: 'fixed',
        inset: 0,
        bgcolor: theme => theme.palette.background.default,
        display: 'flex',
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
      }}
    >
      <LinearProgress
        sx={{
          position: 'absolute',
          top: 'env(safe-area-inset-top)',
          left: 0,
          right: 0,
          height: 2,
          zIndex: 2,
        }}
      />

      {showRail ? (
        <Box
          aria-hidden
          sx={{
            width: SHELL_RAIL_WIDTH,
            flexShrink: 0,
            // Match the real LeftNav surface (tinted), not a white paper panel.
            bgcolor: 'var(--sidebar-bg)',
            color: 'var(--sidebar-text)',
            borderRight: '1px solid var(--sidebar-border)',
            display: 'flex',
            flexDirection: 'column',
            gap: 1.25,
            p: 2,
          }}
        >
          {/* Brand logo */}
          <Skeleton variant="rounded" height={32} width={132} sx={{ mb: 1 }} />
          {/* Search field */}
          <Skeleton variant="rounded" height={34} sx={{ mb: 0.5 }} />
          {/* Nav items */}
          {Array.from({ length: 7 }).map((_, i) => (
            <Skeleton key={i} variant="rounded" height={32} sx={{ width: `${88 - (i % 3) * 12}%` }} />
          ))}
        </Box>
      ) : null}

      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          bgcolor: 'var(--app-content-bg)',
        }}
      >
        {showShell ? (
          <Box
            aria-hidden
            sx={{
              height: isPhone ? SHELL_MOBILE_TOPBAR_HEIGHT : SHELL_TOPBAR_HEIGHT,
              flexShrink: 0,
              borderBottom: theme => `1px solid ${theme.palette.divider}`,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              px: { xs: 2, sm: 3 },
            }}
          >
            <Skeleton variant="circular" width={32} height={32} />
            <Skeleton variant="text" width={180} sx={{ fontSize: '1.25rem' }} />
          </Box>
        ) : null}

        {showShell ? (
          <Box
            aria-hidden
            sx={{
              flexGrow: 1,
              p: { xs: 2, sm: 3 },
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              minHeight: 0,
            }}
          >
            <Skeleton variant="rectangular" height={120} sx={{ borderRadius: 2, maxWidth: 1200 }} />
            <Skeleton variant="rectangular" height={240} sx={{ borderRadius: 2, maxWidth: 1200 }} />
          </Box>
        ) : null}
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 1,
          pointerEvents: 'none',
        }}
      >
        {t('general.loading')}
      </Typography>
    </Box>
  );
};

export default RouteLoader;
